import { useContext, useEffect } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { sendEmailVerification } from 'firebase/auth';
import { Company, Group } from '@esg/esg-global-types';
import { Theme, useTheme } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { auth } from '../../lib/google/firebase';
import AccountMenu from './AccountMenu';
import { CompanyContext } from '../../context/CompanyContext';
import { UserContext } from '../../context/UserContext';
import { userAllowedCompany } from '../../util/user_access';
import { GroupContext } from '../../context/GroupContext';
import CompanySelect from '../shared/input/select/CompanySelect';
import { CompanyExtended } from '../../lib/app/company';
import syntiro_logo_horizontal_white_text from '../../assets/syntiro-logo-horizontal-white-text.svg';
import PrimaryButton from '../shared/button/PrimaryButton';
import PrimaryNavBar from '../shared/nav/PrimaryNavBar';
import PrimarySkeleton from '../shared/loading/PrimarySkeleton';
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { DrawerLeftContext } from '../../context/DrawerLeftContext';
import PrimaryDivider from '../shared/decoration/PrimaryDivider';
import { MinorHeading } from '../shared/typography/PrimaryTypography';
import {
  CUSTOM_DASHBOARD_VIEW_ID,
  hasCustomDashboard,
  Module,
  View,
  modules,
  resolveViewName
} from '../../lib/app/navigation';
import { isMicrosoftUser } from '../../lib/auth/auth.util';
import PBIRefreshButton from './PBIRefreshButton';
import { PBIRefreshContext } from '../../context/PBIRefreshContext';
import { AskSyntiroContext } from '../../context/AskSyntiroContext';
import AskSyntiroTrigger from '../../features/ask-syntiro/components/AskSyntiroTrigger';

interface NavBarApp {
  companies: Array<CompanyExtended> | null;
  left_menu_width: number | undefined;
  resetAppContext: () => void;
}

/**
 * A static bar at the top of the app used to set the company portal and navigate to various views.
 * @param {Array<CompanyExtended>} companies List of companies to include as company selector options.
 * @param {number | undefined} left_menu_width Optionally set a custom width for the left menu to control nav bar width.
 * @param {(): void} resetAppContext Handler function to set the user, group and company context of the app to null.
 * @returns {JSX.Element}
 */
const NavBarApp = ({ companies, left_menu_width, resetAppContext }: NavBarApp) => {
  const { user } = useContext(UserContext);
  const { group, handleGroupChange } = useContext(GroupContext);
  const { company, handleCompanyChange } = useContext(CompanyContext);
  const { isOpenDrawerLeft, openDrawerLeft, closeDrawerLeft } = useContext(DrawerLeftContext);
  const { onRefresh, isRefreshing } = useContext(PBIRefreshContext);
  const { askSyntiroEnabled, askSyntiroOpen, openAskSyntiro, closeAskSyntiro } =
    useContext(AskSyntiroContext);
  const theme: Theme = useTheme();
  const navigate: NavigateFunction = useNavigate();
  const location: Location = useLocation();

  // Map titles in Navbar belonging to non-module based views.
  const additionalViewTitles: Record<string, string> = {
    '/user-management': 'User Management',
    '/group-management': 'Group Management',
    '/master': 'Master Admin'
  };

  const current_path: string = location.pathname;
  const current_module: Module | undefined = modules.find((module: Module) =>
    current_path.includes(module.route)
  );
  let appBarTitle: string | undefined;

  // Set app bar title depending on if module views, or alternative views, can be found for the current url path.
  if (current_module) {
    const foundView =
      current_module.views.length > 0
        ? current_module.views.find((view: View) => current_path.includes(view.route))
        : undefined;
    appBarTitle = foundView
      ? foundView.id === CUSTOM_DASHBOARD_VIEW_ID && !hasCustomDashboard(company?.id)
        ? undefined
        : resolveViewName(foundView, company?.id)
      : current_module.name;
  } else {
    const additionalViewsKey: string | undefined = Object.keys(additionalViewTitles).find(
      (key: string) => current_path.includes(key)
    );
    appBarTitle = additionalViewsKey ? additionalViewTitles[additionalViewsKey] : undefined;
  }

  const handleHomeNavigation = (): void => {
    navigate('/');
  };

  const handleToggleAskSyntiro = (): void => {
    if (askSyntiroOpen) {
      closeAskSyntiro();
    } else {
      openAskSyntiro();
    }
  };

  const allowed_companies: Array<CompanyExtended> = companies
    ? companies.filter((company: CompanyExtended) => {
        return user && userAllowedCompany(user, company);
      })
    : [];

  const cached_group_id: string | null = localStorage.getItem('current_group');
  const cached_company_id: string | null = localStorage.getItem('current_company');

  const current_group: Group | null = group
    ? group
    : allowed_companies.length > 0
      ? cached_group_id
        ? (allowed_companies.find(
            (company: CompanyExtended) => company.group.id === cached_group_id
          )?.group ?? null)
        : allowed_companies[0].group
      : null;

  const current_company: Company | null = company
    ? company
    : allowed_companies.length > 0
      ? cached_company_id
        ? (allowed_companies.find((company: CompanyExtended) => company.id === cached_company_id) ??
          null)
        : allowed_companies[0]
      : null;

  const current_select_value: CompanyExtended | null =
    current_company && current_group
      ? {
          ...current_company,
          group: current_group
        }
      : null;

  const handleGroupCompanySelect = (
    company: Array<CompanyExtended> | CompanyExtended | null
  ): void => {
    handleGroupChange((company as CompanyExtended).group ?? null);
    handleCompanyChange(company as CompanyExtended);
  };

  useEffect(() => {
    if (current_company && current_group) {
      handleGroupChange(current_group);
      handleCompanyChange(current_company);
    }
  }, [companies]);

  const handleVerify = (): void => {
    const user = auth.currentUser;
    if (user) {
      sendEmailVerification(user);
    }
  };

  return (
    <PrimaryNavBar position="fixed" gradient>
      {user ? (
        <>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => openDrawerLeft()}
            edge="start"
            sx={{ mr: 2, ...(isOpenDrawerLeft && { display: 'none' }) }}
          >
            <MenuIcon height={25} stroke="white" fill="white" />
          </IconButton>

          <IconButton
            color="inherit"
            aria-label="close drawer"
            onClick={() => closeDrawerLeft()}
            edge="start"
            sx={{
              mr: 2,
              ...(!isOpenDrawerLeft && { display: 'none' }),
              ml: isOpenDrawerLeft ? `${left_menu_width}px` : undefined
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        </>
      ) : (
        <PrimarySkeleton variant="circular" opacity={0.5} sx={{ width: 25, height: 25, mr: 2 }} />
      )}
      <Box>
        <img
          src={syntiro_logo_horizontal_white_text}
          alt="Logo"
          height={35}
          onClick={handleHomeNavigation}
          style={{ cursor: 'pointer' }}
        />
      </Box>
      {appBarTitle && (
        <>
          <PrimaryDivider orientation="vertical" sx={{ height: 25, mx: 3 }} opacity={0.5} />
          <MinorHeading weight="Medium" dark>
            {appBarTitle.toUpperCase()}
          </MinorHeading>
        </>
      )}
      {auth.currentUser &&
        !auth.currentUser?.emailVerified &&
        !isMicrosoftUser(auth.currentUser) && (
          <PrimaryButton onClick={handleVerify}>Verify Email</PrimaryButton>
        )}
      <Box sx={{ ml: 'auto', p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box sx={{ marginRight: '40px' }}>
            {(onRefresh || isRefreshing) && (
              <PBIRefreshButton onclick_action={onRefresh} is_refreshing={isRefreshing} />
            )}
          </Box>
          {askSyntiroEnabled && (
            <Box sx={{ mr: 4, flexShrink: 0 }}>
              <AskSyntiroTrigger onClick={handleToggleAskSyntiro} isOpen={askSyntiroOpen} />
            </Box>
          )}
          {companies ? (
            <CompanySelect
              group_by="group.name"
              clearable={false}
              searchable={false}
              input_label=""
              color={theme.palette.primary.main}
              handleChangeCompanies={handleGroupCompanySelect}
              selected_options={current_select_value}
              options={allowed_companies}
              disable_underline={true}
              display_as_logo
            />
          ) : (
            <PrimarySkeleton variant="rounded" sx={{ width: 100, height: 40 }} />
          )}
        </Box>
      </Box>

      <AccountMenu resetAppContext={() => resetAppContext()} />
    </PrimaryNavBar>
  );
};

export default NavBarApp;
